import { z } from 'zod';
import { coercedBoolean, paginationSchema, paginationResponseSchema, sortOrderSchema, } from './helpers';
import { endpointDetailsSchema } from './endpoints';
import { policyKeySchema } from './policies';
import { securityIssueSchema } from './scans';
import { severitySchema } from './shared/severity';
import { osvComponentSchema } from './shared/osv';
import { userSchema } from './user';
import { extensionCapabilitySchema, scoreContributorSchema } from './analyses';
import { knownTotalQuerySchema } from './known-total';
import { ColumnDataTypes, createTableQuerySchema, FilterOperators, FiltersSchema, TableResponseMetaSchema, } from './table';
/*
  outcomeSchema defines the possible outcomes for a finding/alert.
  - prevented: System took enforcement action that stopped execution
  - detected: Threat was identified but not stopped
  - unknown: Unable to classify the outcome
*/
export const outcomeSchema = z.enum(['prevented', 'detected', 'unknown']);
export const outcomeOptions = [
    { value: 'prevented', label: 'Prevented' },
    { value: 'detected', label: 'Detected' },
    { value: 'unknown', label: 'Unknown' },
];
/*
  Allowed filterable fields for organization grouped alerts.
  These fields can be used in bracket notation query params.
*/
export const ORGANIZATION_ALERTS_FILTERABLE_FIELDS = {
    SEVERITY: 'severity',
    TYPE: 'type',
    // KIR-3769: composite "Title" filter. Encodes the (subPolicyKey, type)
    // identity behind the displayed alert title (value shape
    // `<subPolicyKey>::<type>`, empty subPolicyKey → `::<type>`). Distinct from
    // `TYPE`, which filters on the raw finding-type slug: a single slug can back
    // two different displayed titles (e.g. `secret-leakage` →
    // "Secret detected in chat content" vs "Secret-file path detected"), so the
    // type filter can neither surface both titles nor separate their result
    // sets. `type` is left untouched for back-compat; `title` is additive.
    TITLE: 'title',
    DESCRIPTION: 'description',
    LOCATION: 'location',
    FILE_PATH: 'filePath',
    OUTCOME: 'outcome',
    OCCURRENCE_COUNT: 'occurrenceCount',
    SCORE: 'score',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    CLIENT_TYPES: 'clientTypes',
    USERS: 'users',
    // KIR-3035: filter alerts by endpoint (machine) NAME. An alert matches if any
    // of its occurrences carries an endpoint snapshot with one of the selected
    // names. Wired as a row-level bool_or HAVING clause in findings-filter-builder.ts.
    ENDPOINTS: 'endpoints',
    // KIR-3800: filter alerts by the security group of the user behind an
    // occurrence. Values are security group IDs. An alert matches if ANY of its
    // occurrences belongs to a user in one of the selected groups — the same
    // any-of semantics as USERS, since an alert deduplicates across users who may
    // sit in different groups. Gated by `security_groups_policies`.
    SECURITY_GROUPS: 'securityGroups',
};
/**
 * KIR-3908: sentinel value for the Users filter (both grouped alerts and the
 * occurrences drill-down). It is the aggregate "not a real Kirin account"
 * bucket — matching BOTH Discovered users and the Unattributed placeholder
 * (people acting through the Cowork hook with no Kirin account). Real user
 * filter values are UUIDs, so this string cannot collide. The backend
 * findings-filter-builder translates it to `findings.user_id IN (SELECT id FROM
 * users WHERE type <> 'full')` (i.e. discovered OR unattributed, deliberately
 * NOT just `type = 'discovered'`); the frontend injects it as a filter option.
 */
export const DISCOVERED_USERS_FILTER_VALUE = 'discovered';
/*
  organizationScanAlertsSortFieldSchema defines sortable fields for grouped
  alerts within an organization.
*/
export var OrganizationScanAlertsSortField;
(function (OrganizationScanAlertsSortField) {
    OrganizationScanAlertsSortField["LastSeenAt"] = "lastSeenAt";
    OrganizationScanAlertsSortField["FirstSeenAt"] = "firstSeenAt";
    OrganizationScanAlertsSortField["Type"] = "type";
    OrganizationScanAlertsSortField["Severity"] = "severity";
    OrganizationScanAlertsSortField["Outcome"] = "outcome";
    OrganizationScanAlertsSortField["OccurrenceCount"] = "occurrenceCount";
    OrganizationScanAlertsSortField["Score"] = "score";
    OrganizationScanAlertsSortField["FilePath"] = "filePath";
    OrganizationScanAlertsSortField["ClientTypes"] = "clientTypes";
    OrganizationScanAlertsSortField["UserCount"] = "userCount";
    // KIR-3035: sort by the number of distinct endpoints (machines) an alert
    // was seen on. Backed by COUNT(DISTINCT findings.endpoint_id) per group.
    OrganizationScanAlertsSortField["EndpointCount"] = "endpointCount";
    // KIR-3800: sort by security group NAME, not by group count — the column
    // shows a name for the common single-group alert, so alphabetical is what a
    // click on that header is expected to do. Backed by MIN(security_groups.name)
    // per group; an alert with no group collates under the "Organization"
    // label rather than being pinned to the end.
    OrganizationScanAlertsSortField["SecurityGroup"] = "securityGroup";
})(OrganizationScanAlertsSortField || (OrganizationScanAlertsSortField = {}));
export const organizationScanAlertsSortFieldSchema = z.enum(OrganizationScanAlertsSortField);
/*
  alertVisibilitySchema controls which alerts the grouped-alerts endpoints
  return. Replaces the two boolean toggles (`includeMuted` /
  `includeExcluded`) with a single 3-state selector to back the toolbar
  segmented control (KIR-2512):

  - `active`     — only non-muted, non-excluded groups (default; matches the
                   pre-KIR-2512 default-list behavior).
  - `all`        — every group, regardless of suppression state.
  - `suppressed` — only groups that are currently muted OR currently excluded
                   (live-evaluated against `*_until > now()`, no sweeper).

  The legacy `includeMuted` / `includeExcluded` fields remain for
  backward-compat with older clients; when `visibility` is provided it wins
  and the legacy fields are ignored on the server side.
*/
export const alertVisibilitySchema = z.enum(['active', 'all', 'suppressed']);
/*
  organizationScanAlertsQuerySchema validates pagination and sorting for
  grouped alert endpoints.
  
  Filters are parsed from bracket notation query params:
  - severity[includes]=critical,high
  - type[excludes]=secret
  
  The schema uses .passthrough() to allow filter params, then transforms them.
*/
// Add search param to query schema
const organizationScanAlertsQueryBaseSchema = paginationSchema.extend({
    sortBy: organizationScanAlertsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    search: z.string().optional(),
    // When true, muted alerts are included in the response with their muted state.
    // When false (default), muted alerts are filtered out.
    includeMuted: coercedBoolean().optional().default(false),
    // When true, excluded alerts (KIR-2363) are included in the response with
    // their excluded state. When false (default), excluded alerts are filtered
    // out. Independent from `includeMuted`: a single "Show Suppressed Alerts"
    // toggle in the UI will flip both, but the contract treats them as
    // independent so older clients keep working.
    includeExcluded: coercedBoolean().optional().default(false),
    // KIR-2512 unified suppression selector. When provided, the server uses
    // this to decide which subset of groups to return and ignores the legacy
    // `includeMuted` / `includeExcluded` booleans above. Defaults to `active`
    // — same default behavior the legacy booleans produce when both are false.
    visibility: alertVisibilitySchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
/**
 * Per-column operator allow-list for org alerts filters. Narrows the DATE
 * data-type defaults so the First Seen / Last Seen columns reject the
 * `equals`/`notEquals` ("Is") operators — only range and value-less operators
 * are valid, because the minute-resolution picker can never exact-match a
 * stored timestamp.
 */
export const ORGANIZATION_ALERTS_OPERATOR_OVERRIDES = {
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
export const organizationScanAlertsQuerySchema = createTableQuerySchema(organizationScanAlertsQueryBaseSchema, {
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SEVERITY]: ColumnDataTypes.ENUM,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE]: ColumnDataTypes.STRING,
    // KIR-3769: composite title identity — exact-match multi-select (enum).
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE]: ColumnDataTypes.ENUM,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.DESCRIPTION]: ColumnDataTypes.TEXT,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH]: ColumnDataTypes.STRING,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OUTCOME]: ColumnDataTypes.ENUM,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.OCCURRENCE_COUNT]: ColumnDataTypes.NUMBER,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SCORE]: ColumnDataTypes.NUMBER,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES]: ColumnDataTypes.ENUM,
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    // KIR-3035: endpoint-name filter is a textual multi-select (enum).
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS]: ColumnDataTypes.ENUM,
    // KIR-3800: security group id multi-select. Registering the field here is
    // what makes `securityGroups[includes]=...` survive query parsing at all —
    // an unregistered bracket param is dropped silently, so the filter would
    // appear to be a no-op rather than an error.
    [ORGANIZATION_ALERTS_FILTERABLE_FIELDS.SECURITY_GROUPS]: ColumnDataTypes.ENUM,
}, ORGANIZATION_ALERTS_OPERATOR_OVERRIDES);
/*
  Base sort fields for user alert list.
  FilePath is included because the dashboard alerts table supports sorting by file.
*/
export var AlertsSortField;
(function (AlertsSortField) {
    AlertsSortField["LastSeenAt"] = "lastSeenAt";
    AlertsSortField["FirstSeenAt"] = "firstSeenAt";
    AlertsSortField["Type"] = "type";
    AlertsSortField["Severity"] = "severity";
    AlertsSortField["Outcome"] = "outcome";
    AlertsSortField["OccurrenceCount"] = "occurrenceCount";
    AlertsSortField["Score"] = "score";
    AlertsSortField["FilePath"] = "filePath";
    AlertsSortField["ClientTypes"] = "clientTypes";
})(AlertsSortField || (AlertsSortField = {}));
export const alertsSortFieldSchema = z.enum(AlertsSortField);
/*
  alertsQuerySchema validates pagination and sorting for user alert list.
  Kept for backward compatibility with existing callers.
*/
export const alertsQuerySchema = paginationSchema.extend({
    sortBy: alertsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
});
/*
  Allowed filterable fields for user grouped alerts.
  Excludes `users` — this is a single-user view. `clientTypes` is included so
  the dashboard column can filter/sort by IDE/Platform; the backend reuses the
  same per-user findings repository, scoped by userId.
*/
export const USER_ALERTS_FILTERABLE_FIELDS = {
    SEVERITY: 'severity',
    TYPE: 'type',
    // KIR-3769: composite "Title" filter — see the matching field on
    // `ORGANIZATION_ALERTS_FILTERABLE_FIELDS`. Additive; `type` is untouched.
    TITLE: 'title',
    OUTCOME: 'outcome',
    FILE_PATH: 'filePath',
    OCCURRENCE_COUNT: 'occurrenceCount',
    SCORE: 'score',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    CLIENT_TYPES: 'clientTypes',
};
/*
  Dynamic filter option fields for user alerts.
  Only type and filePath have server-side option endpoints.
  Severity is static; score/occurrenceCount are numeric ranges.
*/
export const userAlertsFilterOptionFieldSchema = z.enum([
    USER_ALERTS_FILTERABLE_FIELDS.TYPE,
    // KIR-3769: distinct composite `<subPolicyKey>::<type>` title options.
    USER_ALERTS_FILTERABLE_FIELDS.TITLE,
    USER_ALERTS_FILTERABLE_FIELDS.FILE_PATH,
    USER_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES,
]);
/*
  userAlertsFilterOptionsQuerySchema validates the query for user alert filter options.
*/
export const userAlertsFilterOptionsQuerySchema = z.object({
    field: userAlertsFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/*
  userScanAlertsQuerySchema validates pagination, sorting, filters, and search
  for the user grouped alerts endpoint.

  Uses the same bracket-notation transform as organizationScanAlertsQuerySchema
  but restricted to the fields in USER_ALERTS_FILTERABLE_FIELDS.
*/
const userScanAlertsQueryBaseSchema = paginationSchema.extend({
    sortBy: alertsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    search: z.string().optional(),
    // When true, muted alerts are included in the response with their muted state.
    // When false (default), muted alerts are filtered out.
    includeMuted: coercedBoolean().optional().default(false),
    // When true, excluded alerts (KIR-2363) are included with their excluded
    // state. When false (default), excluded alerts are filtered out. See the
    // matching field on `organizationScanAlertsQueryBaseSchema` for rationale.
    includeExcluded: coercedBoolean().optional().default(false),
    // KIR-2512 unified suppression selector. See the matching field on
    // `organizationScanAlertsQueryBaseSchema` for the full rationale; account
    // and org endpoints accept the same enum values.
    visibility: alertVisibilitySchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
/**
 * Per-column operator allow-list for personal alerts filters. Mirrors
 * {@link ORGANIZATION_ALERTS_OPERATOR_OVERRIDES}: the First Seen / Last Seen
 * columns reject the `equals`/`notEquals` ("Is") operators — only range
 * operators are valid.
 */
export const USER_ALERTS_OPERATOR_OVERRIDES = {
    [USER_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [USER_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
export const userScanAlertsQuerySchema = createTableQuerySchema(userScanAlertsQueryBaseSchema, {
    [USER_ALERTS_FILTERABLE_FIELDS.SEVERITY]: ColumnDataTypes.ENUM,
    [USER_ALERTS_FILTERABLE_FIELDS.TYPE]: ColumnDataTypes.STRING,
    // KIR-3769: composite title identity — exact-match multi-select (enum).
    [USER_ALERTS_FILTERABLE_FIELDS.TITLE]: ColumnDataTypes.ENUM,
    [USER_ALERTS_FILTERABLE_FIELDS.OUTCOME]: ColumnDataTypes.ENUM,
    [USER_ALERTS_FILTERABLE_FIELDS.FILE_PATH]: ColumnDataTypes.STRING,
    [USER_ALERTS_FILTERABLE_FIELDS.OCCURRENCE_COUNT]: ColumnDataTypes.NUMBER,
    [USER_ALERTS_FILTERABLE_FIELDS.SCORE]: ColumnDataTypes.NUMBER,
    [USER_ALERTS_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [USER_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
    [USER_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES]: ColumnDataTypes.ENUM,
}, USER_ALERTS_OPERATOR_OVERRIDES);
/*
  groupedAlertUserSchema extends userSchema with per-user stats for a grouped alert.
  lastSeenAt and occurrenceCount are optional for backward compatibility with backends
  that do not yet return per-user stats.
*/
/*
  KIR-3800: a security group referenced from an alert. Only id + name + deleted
  state — the alerts surface renders the name and filters by the id; the full
  group record lives behind the security-groups API.

  `deleted` matters because the soft delete frees the name: the partial unique
  index only covers live rows, so a deleted "Engineering" and a new "Engineering"
  can both be referenced by alerts at once. Without this flag the two are
  indistinguishable on screen. Optional so flag-off and older payloads still
  parse; absent is read as "not deleted".
*/
export const alertSecurityGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    deleted: z.boolean().optional(),
});
/*
  KIR-3800: sentinel filter value for "user belongs to no security group", so
  the organization scope is a first-class, selectable value in the alerts
  Security Group filter rather than an unreachable hole.

  Server-side the membership LEFT JOIN yields NULL for an ungrouped user, and
  the filter expression COALESCEs that NULL to this sentinel — so
  `securityGroups[includes]=__organization__` matches exactly the ungrouped
  alerts and `[excludes]` drops them. It cannot collide with a real value: group
  ids are uuids.
*/
export const ALERT_ORGANIZATION_SCOPE_FILTER_VALUE = '__organization__';
/*
  Display label for that same scope — used for the filter option, the alert's
  chip, and the ORDER BY collation key, so the value a user sorts by, filters
  on, and reads in the cell are always the same string.
*/
export const ALERT_ORGANIZATION_SCOPE_LABEL = 'Organization';
export const groupedAlertUserSchema = userSchema.extend({
    lastSeenAt: z.string().datetime().optional(),
    occurrenceCount: z.number().int().min(1).optional().default(1),
});
/*
  KIR-3035: per-endpoint entry on a grouped alert. Full endpoint details plus
  occurrenceCount = how many of this alert's occurrences happened on that
  endpoint (drives the "Affected Endpoints" popover count + the View Endpoints
  dialog). Details come from the per-finding snapshot taken at ingest.
*/
export const groupedAlertEndpointSchema = endpointDetailsSchema.extend({
    occurrenceCount: z.number().int().min(0),
    // Owner of the endpoint (registry `user_id`), so the UI can deep-link the
    // endpoint id to Inventory→Users and open that user's endpoints dialog
    // (KIR-3035). Null when the machine is no longer in the registry.
    userId: z.string().nullable(),
});
/*
  KIR-3911: occurrences of a grouped alert that carry NO recorded endpoint
  (findings ingested by older agents, before endpoint reporting), grouped by the
  owning user. Renders as "No endpoint recorded" rows in the endpoint breakdown —
  one row per user, each with its own count. `userId` is null when the finding
  had no user attribution. Optional/flag-gated so flag-off responses still parse.
*/
export const groupedAlertUnrecordedEndpointSchema = z.object({
    userId: z.string().nullable(),
    occurrenceCount: z.number().int().min(0),
});
/*
  mutedAlertStateSchema describes the muted-state fragment attached to a
  grouped alert. `muted` is computed by the backend at read time as
  `mutedAt IS NOT NULL AND (mutedUntil IS NULL OR mutedUntil > now())`,
  so an expired timed mute is reported as `muted: false` even before any
  cleanup job runs. mutedByEmail/mutedByName may be null if the muting user
  was deleted (mutedByUserId is set to NULL via ON DELETE SET NULL).
*/
export const mutedAlertStateSchema = z.object({
    muted: z.boolean(),
    mutedAt: z.string().datetime().nullable(),
    mutedUntil: z.string().datetime().nullable(),
    mutedByUserId: z.uuid().nullable(),
    mutedByEmail: z.email().nullable(),
    mutedByName: z.string().nullable(),
});
/*
  Tolerance window applied to "must be in the future" refinements on
  `mutedUntil` / `excludedUntil`. Without this, a client computing
  `new Date(Date.now() + smallDelta).toISOString()` and sending it over a
  non-zero-RTT link can be rejected as "in the past" because `Date.now()` at
  parse time has already advanced past the encoded timestamp. 5 seconds is
  comfortably above realistic client→server RTT and wall-clock drift, while
  still small enough that no caller can use the tolerance to schedule a
  meaningful past-mute / past-exclude.
*/
export const FUTURE_DATETIME_SKEW_TOLERANCE_MS = 5000;
/*
  muteAlertRequestSchema validates the body of `POST /alerts/:alertKey/mute`.

  - mutedUntil: ISO datetime strictly in the future, or null to mute
    indefinitely. A 5-second tolerance (see
    `FUTURE_DATETIME_SKEW_TOLERANCE_MS`) absorbs RTT/clock skew so callers
    sending "now-ish" timestamps don't see spurious validation failures.

  The `findings_groups` table has no `mute_reason` column, so no `reason`
  field is accepted today. If a future product requirement adds one, it will
  be a separate column + migration; do not add a contract field that the
  backend cannot persist.
*/
export const muteAlertRequestSchema = z.object({
    mutedUntil: z
        .string()
        .datetime()
        .nullable()
        .refine(value => value === null ||
        new Date(value).getTime() >
            Date.now() - FUTURE_DATETIME_SKEW_TOLERANCE_MS, { message: 'mutedUntil must be in the future' }),
});
/*
  excludedAlertStateSchema describes the excluded-state fragment attached to a
  grouped alert (KIR-2363 + KIR-2447). `excluded` is computed by the backend
  at read time as
  `excludedAt IS NOT NULL AND (excludedUntil IS NULL OR excludedUntil > now())`,
  mirroring `mutedAlertStateSchema.muted` exactly: an expired timed exclusion
  is reported as `excluded: false` even before any cleanup job runs (lazy
  expiry — no sweeper job needed). excludedByEmail / excludedByName may be
  null if the excluding user was deleted (excludedByUserId is set to NULL via
  ON DELETE SET NULL).
*/
export const excludedAlertStateSchema = z.object({
    excluded: z.boolean(),
    excludedAt: z.string().datetime().nullable(),
    excludedUntil: z.string().datetime().nullable(),
    excludedByUserId: z.uuid().nullable(),
    excludedByEmail: z.email().nullable(),
    excludedByName: z.string().nullable(),
});
/*
  excludeAlertRequestSchema validates the body of
  `POST /alerts/:alertKey/exclude` (KIR-2363 + KIR-2447). Mirrors
  `muteAlertRequestSchema` exactly:

  - excludedUntil: ISO datetime strictly in the future, or null to exclude
    indefinitely. Same `FUTURE_DATETIME_SKEW_TOLERANCE_MS` applies — keep
    the two refines symmetric so RTT/clock-skew handling never drifts
    between mute and exclude.

  The `findings_groups` table has no `exclude_reason` column, so no `reason`
  field is accepted today. If a future product requirement adds one, it
  will be a separate column + migration; do not add a contract field that
  the backend cannot persist.
*/
export const excludeAlertRequestSchema = z.object({
    excludedUntil: z
        .string()
        .datetime()
        .nullable()
        .refine(value => value === null ||
        new Date(value).getTime() >
            Date.now() - FUTURE_DATETIME_SKEW_TOLERANCE_MS, { message: 'excludedUntil must be in the future' }),
});
/*
  Set of finding `type` values that CANNOT be silenced via exclusion. These
  alert types are enforced client-side by the IDE/agent hook layer (e.g. Cursor
  `BeforeSubmitPrompt` / Claude `PreToolUse` / Copilot `PreToolUse`) BEFORE the
  scan reaches the backend, so the backend's `applyExclusionOverridesToIssues`
  override (which sets `issue.allowed = true` and clears `issue.fix`) has no
  way to retract the deny decision the agent has already received.

  Currently scoped to `secret-leakage`, the only finding type wired through a
  hook policy today (mapped from `POLICY_KEY_SECRET_DETECTION` in
  `extensions/vscode-core/src/services/hooks/processHookRequest.ts`).
*/
export const NON_EXCLUDABLE_FINDING_TYPES = new Set([
    'secret-leakage',
]);
/**
 * Returns `true` when the given finding `type` may be excluded via the
 * `POST /alerts/:alertKey/exclude` endpoint. Empty/nullish input is treated
 * as excludable so missing telemetry never accidentally blocks the action.
 */
export function isExcludableFindingType(type) {
    if (!type)
        return true;
    return !NON_EXCLUDABLE_FINDING_TYPES.has(type);
}
/**
 * Single source of truth for the user-facing copy explaining why exclusion is
 * unavailable for a given finding. Consumed by the FE button tooltip and the
 * BE 400 error so messaging stays aligned.
 */
export const NON_EXCLUDABLE_REASON = 'Not Available';
/*
  alertSourceDetailsSchema (KIR-3529) — the structured, masked "DETECTED ON
  CLIENT" panel for Extension / MCP alerts. Built backend-side from the finding's
  identity (Allow/Block context, normalized location, parsed MCP config) via the
  shared identity functions + masking, so each field's value is exactly what the
  Allow/Block matcher uses.

  Field semantics (resolves design review):
  - `key` is the Allow/Block `rule_object` key (`name`/`publisher`/`version`/
    `type`); NO display `label` is carried — the frontend resolves labels from
    the Allow/Block column config so wording is single-sourced.
  - `detected` is the raw value Kirin observed; `allowBlockValue` is the
    normalized, copy-into-editor value. The UI shows both only when they differ.
  - `isUnknown: true` means no verifiable value — the UI renders the word
    "Unknown"; the backend never stores the literal string "Unknown".
  - `masked: true` flags that a sensitive fragment was redacted in the value.
*/
export const ALERT_SOURCE_FIELD_KEYS = {
    NAME: 'name',
    PUBLISHER: 'publisher',
    VERSION: 'version',
    TYPE: 'type',
};
export const alertSourceFieldSchema = z.object({
    key: z.string(),
    detected: z.string().optional(),
    allowBlockValue: z.string().optional(),
    isUnknown: z.boolean(),
    masked: z.boolean().optional(),
});
/*
  Masked file/IDE source for the alert. `display` is the shortened/masked path
  (file basename + private parents collapsed to `…/`); `ide` is the
  human-readable IDE/config source label (e.g. "Cursor", "VS Code").
*/
export const alertSourceFileSchema = z.object({
    display: z.string(),
    ide: z.string().optional(),
});
export const extensionSourceDetailsSchema = z.object({
    kind: z.literal('extension'),
    fields: z.array(alertSourceFieldSchema),
    sourceFile: alertSourceFileSchema.optional(),
    /**
     * KIR-3781: whether the publisher is in TRUSTED_SOURCES. Stamped at build
     * time so the FE does not re-implement the trust list. Optional for legacy
     * rows written before this field existed.
     */
    publisherTrusted: z.boolean().optional(),
    /**
     * KIR-4070: the OSV/CVE/GHSA id for a "Vulnerable dependency in extension"
     * finding (e.g. `GHSA-…`, `CVE-…`). Present only on `osv_vulnerability`
     * findings; the panel renders it plus a `https://osv.dev/vulnerability/<id>`
     * advisory link. Optional for every other extension finding.
     */
    osvId: z.string().optional(),
    /** KIR-4190: see `osvComponentSchema`. */
    osvComponent: osvComponentSchema.optional(),
});
export const mcpSourceDetailsSchema = z.object({
    kind: z.literal('mcp'),
    fields: z.array(alertSourceFieldSchema),
    sourceFile: alertSourceFileSchema.optional(),
    /**
     * KIR-4190: advisory id for a "Vulnerable dependency in MCP server" finding,
     * so the panel can link to `osv.dev`. The extension surface got this in
     * KIR-4070; the MCP surface had no structured id at all.
     */
    osvId: z.string().optional(),
    /** KIR-4190: see `osvComponentSchema`. */
    osvComponent: osvComponentSchema.optional(),
    // Pre-masked JSON block that defines this MCP server (the "More details"
    // panel). Secrets (tokens / Authorization / Bearer / credentials) are
    // already redacted by the backend before persistence.
    configJsonBlock: z.string().optional(),
    // KIR-4175: evidence for a `suspicious_description` regex finding — the rule
    // reason that fired (`matchedRule`), the literal matched span
    // (`matchedText`), and the surrounding context split into `before`/`after`
    // so the panel renders `before <mark>match</mark> after` (exact highlight, no
    // client re-search). `toolDescription` is the untruncated text for see/copy.
    // Present only on regex MCP findings; display-only (never part of the
    // finding_key). Secrets are redacted from every field before persistence.
    matchedRule: z.string().optional(),
    matchedText: z.string().optional(),
    matchedContextBefore: z.string().optional(),
    matchedContextAfter: z.string().optional(),
    toolDescription: z.string().optional(),
});
// PII detection (KIR-3034): a redacted excerpt of the scanned content for
// chat-side alerts with no file to point at (prompt / terminal / MCP output).
// Every PII value is already replaced by `[REDACTED:<type>]` backend-side, so
// this block never carries a raw value. File-read alerts name the file instead
// and carry no sample.
export const piiSourceDetailsSchema = z.object({
    kind: z.literal('pii'),
    redactedSample: z.string(),
});
export const alertSourceDetailsSchema = z.discriminatedUnion('kind', [
    extensionSourceDetailsSchema,
    mcpSourceDetailsSchema,
    piiSourceDetailsSchema,
]);
/**
 * KIR-3781: catalog weighted-score breakdown for extension-security alerts.
 * Distinct from the existing severity-mapped `score` (0–100). Omit when the
 * finding has no persisted contributors (legacy / custom_capability_rule).
 */
export const alertScoreBreakdownSchema = z.object({
    score: z.number().int().min(0).optional(),
    contributors: z.array(scoreContributorSchema),
});
/*
  groupedAlertSchema defines the structure of a grouped alert returned for an
  organization. filePath, score, occurrenceCount, and clientTypes are optional
  for backward compatibility; UI shows "—" when backend does not send them.
  clientTypes is the list of IDE/client names (e.g. cursor, vscode) that have
  at least one occurrence of this alert.
  `mute` is the muted-state fragment, omitted entirely when the alert_muting
  feature flag is off so older backends/responses remain valid.
  `exclude` is the excluded-state fragment (KIR-2363); same omission rule
  applies for the `alert_ignore` feature flag. `mute` and `exclude` are
  independent — a row may carry both, either, or neither.

  `policyKey` and `subPolicyKey` (KIR-2378) carry the policy / sub-policy that
  triggered the alert. The backend computes `policyKey` at the DTO boundary
  from the persisted `sub_policy_key` (with a `type -> policyKey` fallback
  when `sub_policy_key IS NULL`), so consumers can render the parent policy
  without a registry lookup. Both fields are optional during the rollout — a
  pre-KIR-2378 backend response will omit them; once the BE always emits
  `policyKey` we will tighten the contract in a follow-up release.
*/
/*
  alertProjectSchema (KIR-3765) — the projects-inventory entity a secret alert
  was attributed to, resolved from the scan's git origin at ingest. Contains no
  local path (privacy): only the normalized origin + inventory identity. `slug`
  deep-links to the single-project page. Omitted for local-only repos (no
  remote) and when the secret-detection-details flag is off.
*/
export const alertProjectSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    repoOriginUrl: z.string().nullable(),
    slug: z.string(),
});
// KIR-3765: representative-only note attached to every enriched grouped field
// so API consumers (not just the UI) know these reflect the latest occurrence
// and are NOT aggregated across a heterogeneous group.
const REPRESENTATIVE_NOTE = 'Representative (latest) occurrence value; not aggregated across the group';
export const groupedAlertSchema = z.object({
    alertKey: z.string(),
    type: z.string(),
    severity: severitySchema,
    description: z.string(),
    location: z.string().nullable(),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
    users: z.array(groupedAlertUserSchema).min(1),
    // KIR-3800: every scope this alert's occurrences span. An alert can span
    // several groups because it deduplicates across users; the group is
    // deliberately NOT part of the dedup key. Occurrences produced outside any
    // group add an explicit entry whose id is
    // `ALERT_ORGANIZATION_SCOPE_FILTER_VALUE`, so a partially-ungrouped alert
    // lists both its real group and Organization and can never contradict the
    // Organization filter. A present array is therefore never empty.
    // Optional + flag-gated, so flag-off responses stay byte-identical.
    securityGroups: z.array(alertSecurityGroupSchema).optional(),
    // KIR-3035: distinct endpoints this alert occurred on, each with its alert
    // count. Optional so flag-off / pre-feature responses still parse.
    endpoints: z.array(groupedAlertEndpointSchema).optional(),
    // KIR-3911: per-user counts of this alert's occurrences with NO recorded
    // endpoint (older agents). Drives the "No endpoint recorded" rows in the
    // endpoint breakdown. Optional/flag-gated.
    endpointsUnrecorded: z.array(groupedAlertUnrecordedEndpointSchema).optional(),
    filePath: z.string().optional(),
    score: z.number().int().min(0).max(100).optional(),
    occurrenceCount: z.number().int().min(1).optional(),
    outcome: outcomeSchema.optional(),
    clientTypes: z.array(z.string()).optional(),
    mute: mutedAlertStateSchema.optional(),
    exclude: excludedAlertStateSchema.optional(),
    policyKey: policyKeySchema.optional(),
    subPolicyKey: z.string().nullable().optional(),
    // KIR-3529: masked Extension/MCP source details. On the grouped card these
    // are computed from the representative (latest) occurrence; per-occurrence
    // truth lives on `alertOccurrenceSchema.sourceDetails`. Optional +
    // flag-gated — omitted when the alert is not an extension/MCP alert or the
    // feature flag is off.
    // `.catch(undefined)`: an unknown/incompatible sourceDetails variant (e.g. a
    // newer `kind` seen by an older client mid-deploy) degrades to "no panel"
    // rather than failing the whole alerts-list parse (KIR-3034).
    sourceDetails: alertSourceDetailsSchema.optional().catch(undefined),
    // KIR-3765 secret-detection enrichment. Representative (latest) occurrence
    // values, flag-gated behind `secret_detection_details`. Omitted when the
    // flag is off or the alert carries no enrichment.
    engine: z.string().optional().describe(REPRESENTATIVE_NOTE),
    ruleId: z.string().optional().describe(REPRESENTATIVE_NOTE),
    // KIR-3765: readable rule name for Kirin (regex/file-pattern) rules; omitted
    // for gitleaks (its ruleId is already readable).
    ruleName: z.string().optional().describe(REPRESENTATIVE_NOTE),
    entropy: z.number().optional().describe(REPRESENTATIVE_NOTE),
    toolName: z.string().optional().describe(REPRESENTATIVE_NOTE),
    sessionId: z.string().optional().describe(REPRESENTATIVE_NOTE),
    workspaceName: z.string().optional().describe(REPRESENTATIVE_NOTE),
    // KIR-3765: first-class tool-event side (`pre`/`post`) so the UI derives the
    // Tool Input/Output label without parsing the `file_path` sentinel.
    eventKind: z.enum(['pre', 'post']).optional().describe(REPRESENTATIVE_NOTE),
    // KIR-3765: backend-built, doubly-redacted context snippet (~50 chars either
    // side of the secret; secret + PII masked). Best-effort earliest-occurrence
    // excerpt; raw content is never stored. Flag-gated behind
    // `secret_detection_details`.
    matchContext: z.string().optional().describe(REPRESENTATIVE_NOTE),
    project: alertProjectSchema.optional().describe(REPRESENTATIVE_NOTE),
    // KIR-3781: catalog aggregate score + contributors. Do not confuse with
    // `score` above (severity→0–100 mapping).
    scoreBreakdown: alertScoreBreakdownSchema.optional(),
});
/*
  groupedAlertsResponseSchema defines the response for grouped alerts.
*/
/**
 * List response schema for grouped alerts.
 * Shape: { alerts: GroupedAlert[], pagination: {...} }
 */
export const groupedAlertsListResponseSchema = z.object({
    alerts: z.array(groupedAlertSchema),
    pagination: paginationResponseSchema,
});
/**
 * Table engine response schema for grouped alerts.
 * Shape: { data: GroupedAlert[], pagination: {...}, meta: {...} }
 */
export const groupedAlertsTableResponseSchema = z.object({
    data: z.array(groupedAlertSchema),
    pagination: paginationResponseSchema,
    meta: TableResponseMetaSchema,
});
/**
 * Combined response schema for grouped alerts.
 * Validates either list shape (alerts field) or table engine shape (data field).
 *
 * - List: { alerts: GroupedAlert[], pagination: {...} }
 * - Table engine: { data: GroupedAlert[], pagination: {...}, meta: {...} }
 */
export const groupedAlertsResponseSchema = z.union([
    groupedAlertsListResponseSchema,
    groupedAlertsTableResponseSchema,
]);
/*
  alertOccurrenceSchema defines an individual occurrence of an alert.
  Extends securityIssueSchema with scan context for drill-down views.
  score, clientVersion, and clientType are optional for backward compatibility;
  UI shows "—" when backend does not send them.
  clientType is the IDE/client that ran the scan (cursor, vscode, claude, copilot).
*/
export const alertOccurrenceSchema = securityIssueSchema.extend({
    scanId: z.uuid(),
    user: userSchema,
    filePath: z.string(),
    fileType: z.string(),
    scannedAt: z.string().datetime(),
    score: z.number().int().min(0).max(100).optional(),
    outcome: outcomeSchema.optional(),
    clientVersion: z.string().nullable().optional(),
    clientType: z.string().nullable().optional(),
    // KIR-3035: the endpoint (machine) this occurrence happened on. Optional +
    // nullable for pre-feature findings that carry no endpoint.
    endpointId: z.string().nullable().optional(),
    endpointName: z.string().nullable().optional(),
    // KIR-3529: this occurrence's own masked source details (authoritative
    // per-occurrence values for the occurrences drill-down).
    // `.catch(undefined)`: an unknown/incompatible sourceDetails variant (e.g. a
    // newer `kind` seen by an older client mid-deploy) degrades to "no panel"
    // rather than failing the whole alerts-list parse (KIR-3034).
    sourceDetails: alertSourceDetailsSchema.optional().catch(undefined),
    // KIR-3765: the project this occurrence was attributed to (git-remote only,
    // resolved from the scan origin). The per-finding enrichment fields
    // (engine/ruleId/ruleName/entropy/toolName/sessionId/workspaceName/eventKind)
    // are inherited from `securityIssueSchema`. Flag-gated behind
    // `secret_detection_details`.
    project: alertProjectSchema.optional(),
    // KIR-3800: the security group of the user behind THIS occurrence. An
    // occurrence is one event by one user, so it maps to exactly one group —
    // `null` when that user belongs to none (the UI renders the organization
    // scope). Optional and flag-gated, so flag-off responses keep their shape.
    securityGroup: alertSecurityGroupSchema.nullable().optional(),
    // KIR-3781: catalog aggregate score + contributors (not severity-mapped score).
    scoreBreakdown: alertScoreBreakdownSchema.optional(),
});
/*
  Allowed filterable fields for alert occurrences drill-down.
  Only fields that have a direct column on each occurrence row are applicable.
  Fields like severity/outcome/type are constant across all occurrences of a
  grouped alert and are therefore excluded.
*/
export const ALERT_OCCURRENCES_FILTERABLE_FIELDS = {
    USERS: 'users',
    LAST_SEEN_AT: 'lastSeenAt',
    FILE_PATH: 'filePath',
    CLIENT_TYPES: 'clientTypes',
    SCORE: 'score',
};
/*
  alertOccurrencesQuerySchema validates pagination and optional filters for the
  alert occurrences drill-down endpoint.

  Filters use the same bracket-notation as the main alerts table:
  - users[includes]=user-id-1,user-id-2
  - filePath[contains]=some-path
  - clientTypes[includes]=cursor,vscode
  - score[includes]=50,100  (min,max range)
  - lastSeenAt[between]=2024-01-01,2024-12-31
*/
export const alertOccurrencesQuerySchema = createTableQuerySchema(paginationSchema, {
    [ALERT_OCCURRENCES_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    [ALERT_OCCURRENCES_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
    [ALERT_OCCURRENCES_FILTERABLE_FIELDS.FILE_PATH]: ColumnDataTypes.STRING,
    [ALERT_OCCURRENCES_FILTERABLE_FIELDS.CLIENT_TYPES]: ColumnDataTypes.ENUM,
    [ALERT_OCCURRENCES_FILTERABLE_FIELDS.SCORE]: ColumnDataTypes.NUMBER,
});
/*
  alertOccurrencesResponseSchema defines the response for alert occurrences
  drill-down, including the parent grouped alert and its individual occurrences.
  `totalUnfiltered` is the total number of occurrences without filters applied,
  allowing the UI to show "Showing X of Y variations" when filters are active.
*/
export const alertOccurrencesResponseSchema = z.object({
    alert: groupedAlertSchema,
    occurrences: z.array(alertOccurrenceSchema),
    pagination: paginationResponseSchema,
    totalUnfiltered: z.number().int().min(0).optional(),
    capabilities: z.array(extensionCapabilitySchema).optional(),
});
/*
  Filter option fields schema for dynamic filter options.
  Uses TYPE, FILE_PATH, CLIENT_TYPES, and USERS from ORGANIZATION_ALERTS_FILTERABLE_FIELDS.
  Excludes SEVERITY as it has static options.
*/
export const alertFilterOptionFieldSchema = z.enum([
    ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TYPE,
    // KIR-3769: distinct composite `<subPolicyKey>::<type>` title options.
    ORGANIZATION_ALERTS_FILTERABLE_FIELDS.TITLE,
    ORGANIZATION_ALERTS_FILTERABLE_FIELDS.FILE_PATH,
    ORGANIZATION_ALERTS_FILTERABLE_FIELDS.CLIENT_TYPES,
    ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS,
    // KIR-3035: distinct endpoint names for the Endpoints filter dropdown.
    ORGANIZATION_ALERTS_FILTERABLE_FIELDS.ENDPOINTS,
]);
/*
  alertFilterOptionsQuerySchema validates the query for filter options.
  Requires a field parameter to specify which field's options to retrieve.
  Optional search parameter for filtering options.
*/
export const alertFilterOptionsQuerySchema = z.object({
    field: alertFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/*
  alertFilterOptionsResponseSchema defines the response for filter options.
  Returns an array of string values (not objects with value/label).
  Frontend can format labels using labelFormatter function.
*/
export const alertFilterOptionsResponseSchema = z.object({
    field: alertFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
// ---------------------------------------------------------------------------
// Bulk alert actions (KIR-3252)
// ---------------------------------------------------------------------------
/*
  Bulk selection + state-aware bulk actions for the Alerts screen.

  Finalized design (KIR-3252):
  - All-or-nothing. Each action is ONE set-based statement on the backend; there
    is no partial-success / per-row failure reporting and no undo. The change /
    skip / ineligible breakdown is surfaced BEFORE the action via the eligibility
    preview (`bulkAlertEligibilityResponseSchema`) — the confirmation modal and
    bulk-action bar — not after it.
  - Selection travels as a descriptor, not always an id list: `keys` for explicit
    row / current-page selection, `query` for "select all matching the current
    filter across pages" (resolved server-side from the same filters/search the
    list endpoint accepts).
  - Per-row audit comes free from the `findings_groups` UPDATE trigger; there is
    no parent/child bulk-action audit linking.
*/
export const bulkAlertActionSchema = z.enum([
    'mute',
    'unmute',
    'exclude',
    'remove-exclusion',
]);
/*
  Hard ceiling on an explicit `keys` selection. Bounds the bind-param count and
  lock duration of the single set-based write. The FE never needs to exceed it:
  "select all across pages" uses `query` mode instead of enumerating keys.
*/
export const BULK_ALERT_MAX_KEYS = 10000;
export const bulkAlertSelectionByKeysSchema = z.object({
    mode: z.literal('keys'),
    alertKeys: z.array(z.string().min(1)).min(1).max(BULK_ALERT_MAX_KEYS),
});
export const bulkAlertSelectionByQuerySchema = z.object({
    mode: z.literal('query'),
    // Same column filters + search the grouped-alerts list endpoint accepts; the
    // backend resolves them to the full matching key set across pages.
    filters: FiltersSchema.optional().default([]),
    search: z.string().optional(),
    // The current view's suppression selector. REQUIRED for "select all across
    // pages" to match exactly what the list shows: in `suppressed` it resolves
    // only the muted/excluded alerts, in `active` only the non-suppressed ones.
    // Omitting it resolves every matching alert regardless of suppression state
    // (the pre-KIR-3252 default-list behaviour).
    visibility: alertVisibilitySchema.optional(),
    // "Select all across pages, EXCEPT these" — alert keys the user explicitly
    // deselected from the all-matching set. The backend resolves the query and
    // removes these keys, so the action applies to (matching − excludeKeys).
    excludeKeys: z.array(z.string().min(1)).max(BULK_ALERT_MAX_KEYS).optional(),
});
export const bulkAlertSelectionSchema = z.discriminatedUnion('mode', [
    bulkAlertSelectionByKeysSchema,
    bulkAlertSelectionByQuerySchema,
]);
/*
  bulkAlertApplyRequestSchema validates the body of `POST /alerts/bulk/:action`.
  The action itself comes from the path param. `mutedUntil` is consumed only
  when action = 'mute' and `excludedUntil` only when action = 'exclude'; the
  irrelevant duration field is ignored by the handler (no cross-field refine, so
  a client may safely omit both for unmute / remove-exclusion). Both reuse the
  exact future-datetime refine + `FUTURE_DATETIME_SKEW_TOLERANCE_MS` from the
  single-alert mute/exclude schemas so RTT/clock-skew handling never drifts.
*/
export const bulkAlertApplyRequestSchema = z.object({
    selection: bulkAlertSelectionSchema,
    mutedUntil: z
        .string()
        .datetime()
        .nullable()
        .refine(value => value === null ||
        new Date(value).getTime() >
            Date.now() - FUTURE_DATETIME_SKEW_TOLERANCE_MS, { message: 'mutedUntil must be in the future' })
        .optional(),
    excludedUntil: z
        .string()
        .datetime()
        .nullable()
        .refine(value => value === null ||
        new Date(value).getTime() >
            Date.now() - FUTURE_DATETIME_SKEW_TOLERANCE_MS, { message: 'excludedUntil must be in the future' })
        .optional(),
});
/*
  Minimal apply result. All-or-nothing → no per-row applied/skipped/failed
  lists. `changedCount` is the number of alerts that actually transitioned
  (the guarded UPDATE's RETURNING count); already-in-target-state and
  exclusion-ineligible rows are silently excluded (they were disclosed in the
  pre-action eligibility preview).
*/
export const bulkAlertApplyResponseSchema = z.object({
    action: bulkAlertActionSchema,
    changedCount: z.number().int().min(0),
});
export const bulkAlertEligibilityRequestSchema = z.object({
    selection: bulkAlertSelectionSchema,
});
/*
  Pre-action eligibility preview — the count surface that drives the bulk-action
  bar labels and the confirmation-modal copy (the "trade-up" for dropping
  post-action per-row detail). Counts are over the resolved selection
  (`totalSelected` distinct alert keys).

  The nine buckets are exactly those enumerated in the ticket. Documented
  identities consumers can rely on:
    eligibleForMute  + alreadyMuted    === totalSelected
    eligibleForUnmute === alreadyMuted
    alreadyUnmuted    === eligibleForMute
    eligibleForExclusion + alreadyExcluded + exclusionIneligible === totalSelected
    eligibleForRemoveExclusion === alreadyExcluded
    withoutExclusion  === eligibleForExclusion + exclusionIneligible
*/
export const bulkAlertEligibilityResponseSchema = z.object({
    totalSelected: z.number().int().min(0),
    eligibleForMute: z.number().int().min(0),
    alreadyMuted: z.number().int().min(0),
    eligibleForUnmute: z.number().int().min(0),
    alreadyUnmuted: z.number().int().min(0),
    eligibleForExclusion: z.number().int().min(0),
    alreadyExcluded: z.number().int().min(0),
    eligibleForRemoveExclusion: z.number().int().min(0),
    withoutExclusion: z.number().int().min(0),
    exclusionIneligible: z.number().int().min(0),
});
//# sourceMappingURL=alerts.js.map